import { Button, Card, CardContent, CardHeader, CardTitle } from "@devhop/ui";
import { format } from "date-fns";
import {
  ArrowLeft,
  Download,
  ExternalLink,
  HelpCircle,
  Pencil,
} from "lucide-react";
import { useParams, useNavigate } from "@tanstack/react-router";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import { getLaoMonthName } from "@/shared/lib/date-time";
import { monthlyReportApi } from "../api/client";
import { useMonthlyReportById } from "../api/queries";
import {
  MonthlyReportTour,
  monthlyReportDetailTourSteps,
  useMonthlyReportTour,
} from "../tour";

function formatDate(s: string) {
  try {
    return format(new Date(s), "dd/MM/yyyy HH:mm");
  } catch {
    return s;
  }
}

export function MonthlyReportDetailPage() {
  const params = useParams({ strict: false }) as { id: string };
  const nav = useNavigate({ from: "/app/management/monthly-reports/$id" });
  const { data: report, isLoading, isError } = useMonthlyReportById(params.id);
  const permissions = usePermissions();
  const canUpdate = permissions.has(Permissions.mgmt_monthly_report.update);
  const tour = useMonthlyReportTour("detail");

  if (isLoading) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex items-center justify-center py-12">
            <p className="text-muted-foreground">ກຳລັງໂຫຼດ...</p>
          </div>
        </Main>
      </>
    );
  }

  if (isError || !report) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex flex-col items-center justify-center gap-4 py-12">
            <p className="text-destructive">ບໍ່ພົບລາຍງານ</p>
            <button
              type="button"
              className="text-primary hover:underline"
              onClick={() =>
                nav({
                  to: "/app/management/monthly-reports",
                  search: {
                    offset: 0,
                    limit: 20,
                    sort: undefined,
                    periodYear: undefined,
                    periodMonth: undefined,
                    departmentId: undefined,
                    search: undefined,
                  },
                })
              }
            >
              ກັບໄປລາຍການ
            </button>
          </div>
        </Main>
      </>
    );
  }

  const hasFile = Boolean(report.fileKey);
  const hasLink = Boolean(report.documentUrl);

  return (
    <>
      <Header />
      <Main>
        <MonthlyReportTour
          steps={monthlyReportDetailTourSteps}
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-semibold text-2xl">
                  ລາຍລະອຽດລາຍງານປະຈໍາເດືອນ
                </h1>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 rounded-full"
                  onClick={tour.startTour}
                  title="ເລີ່ມທົດສອບການນຳທາງ"
                  aria-label="ເລີ່ມທົດສອບການນຳທາງ"
                >
                  <HelpCircle className="size-5" />
                </Button>
              </div>
              <p className="text-muted-foreground text-sm">ເບິ່ງຂໍ້ມູນລາຍງານ</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              data-tourid="monthly-report-detail-back"
              onClick={() =>
                nav({
                  to: "/app/management/monthly-reports",
                  search: {
                    offset: 0,
                    limit: 20,
                    sort: undefined,
                    periodYear: undefined,
                    periodMonth: undefined,
                    departmentId: undefined,
                    search: undefined,
                  },
                })
              }
            >
              <ArrowLeft className="h-4 w-4" /> ກັບໄປລາຍການ
            </Button>
            {canUpdate && (
              <Button
                variant="default"
                onClick={() =>
                  nav({
                    to: "/app/management/monthly-reports/$id/edit",
                    params: { id: report.id },
                  })
                }
                className="gap-2"
              >
                <Pencil className="h-4 w-4" />
                ແກ້ໄຂ
              </Button>
            )}
          </div>
        </div>
        <div
          className="mx-auto max-w-3xl space-y-6"
          data-tourid="monthly-report-detail-content"
        >
          <Card>
            <CardHeader>
              <CardTitle>ຂໍ້ມູນລາຍງານປະຈໍາເດືອນ</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <p className="text-muted-foreground text-sm">ປີ</p>
                  <p className="font-medium">{report.periodYear}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-sm">ເດືອນ</p>
                  <p className="font-medium">
                    {getLaoMonthName(report.periodMonth)} ({report.periodMonth})
                  </p>
                </div>
              </div>

              <div>
                <p className="text-muted-foreground text-sm">ພະແນກ</p>
                <p className="font-medium">{report.departmentName ?? "—"}</p>
              </div>

              <div>
                <p className="text-muted-foreground text-sm">
                  ຊື່ລາຍງານ / ຄຳອະທິບາຍ
                </p>
                <p className="font-medium">
                  {(report.title ?? "").trim() ? report.title : "—"}
                </p>
              </div>

              <div className="space-y-4">
                <p className="text-muted-foreground text-sm">ເອກະສານ</p>
                <div className="flex flex-wrap gap-2">
                  {hasFile && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        const url = monthlyReportApi.getFileUrl(
                          report.fileKey ?? "",
                        );
                        window.open(url, "_blank");
                      }}
                      className="gap-2"
                    >
                      <Download className="h-4 w-4" />
                      ດາວໂຫຼດເອກະສານ
                    </Button>
                  )}
                  {hasLink && (
                    <Button
                      variant="outline"
                      onClick={() =>
                        window.open(report.documentUrl ?? "", "_blank")
                      }
                      className="gap-2"
                    >
                      <ExternalLink className="h-4 w-4" />
                      ເປີດລິ້ງ
                    </Button>
                  )}
                  {!hasFile && !hasLink && (
                    <span className="text-muted-foreground text-sm">—</span>
                  )}
                </div>
              </div>

              <div>
                <p className="text-muted-foreground text-sm">ຜູ້ສ້າງ</p>
                <p className="font-medium">{report.createdByName ?? "—"}</p>
              </div>

              <div>
                <p className="text-muted-foreground text-sm">ວັນທີສ້າງ/ອັບເດດ</p>
                <p className="font-medium">{formatDate(report.updatedAt)}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </Main>
    </>
  );
}
